import React, { useState, useEffect, useCallback } from 'react';
import { medicationApprovalAPI, patientAPI, admissionAPI } from '../../lib/api';
import { formatDateTime, apiErrorMessage } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { Pill, Plus, Check, X, AlertCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../../lib/logger';

const MedicationApprovals = () => {
  const { user, hasRole } = useAuth();
  const [approvals, setApprovals] = useState([]);
  const [admissions, setAdmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [formData, setFormData] = useState({
    patient_id: '',
    admission_id: '',
    medication_name: '',
    dosage: '',
    frequency: '',
    duration: '',
    reason: '',
    urgency: 'normal',
  });

  const canApprove = hasRole(['super_admin', 'admin', 'doctor']);
  const canRequest = hasRole(['super_admin', 'admin', 'doctor', 'nurse']);

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const [appRes, admRes] = await Promise.all([
        medicationApprovalAPI.getAll(params),
        admissionAPI.getAll({ status: 'admitted' }),
      ]);
      setApprovals(appRes.data.approvals || appRes.data || []);
      setAdmissions(admRes.data.admissions || admRes.data || []);
    } catch (error) {
      logger.error('Error loading medication approvals:', error);
      toast.error(apiErrorMessage(error, 'Failed to load medication approvals. Please try again.'));
      setApprovals([]);
      setAdmissions([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);


  useEffect(() => {
    fetchData();
  }, [statusFilter, fetchData]);
  const handleAdmissionChange = (admissionId) => {
    const admission = admissions.find(a => a.id === admissionId);
    setFormData({
      ...formData,
      admission_id: admissionId,
      patient_id: admission?.patient_id || '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await medicationApprovalAPI.create(formData);
      toast.success('Medication approval request submitted');
      setDialogOpen(false);
      setFormData({
        patient_id: '',
        admission_id: '',
        medication_name: '',
        dosage: '',
        frequency: '',
        duration: '',
        reason: '',
        urgency: 'normal',
      });
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail 
        ? (Array.isArray(error.response.data.detail) 
            ? error.response.data.detail.map(e => e.msg || e).join(', ')
            : error.response.data.detail)
        : 'Failed to submit request';
      toast.error(errorMsg);
    }
  };

  const handleApprove = async (approvalId) => {
    try {
      const response = await medicationApprovalAPI.approve(approvalId);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to approve');
    }
  };

  const openRejectDialog = (approval) => {
    setSelectedApproval(approval);
    setRejectionReason('');
    setRejectDialogOpen(true);
  };

  const handleReject = async () => {
    try {
      await medicationApprovalAPI.reject(selectedApproval.id, { reason: rejectionReason });
      toast.success('Medication request rejected');
      setRejectDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to reject');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
    };
    return styles[status] || 'bg-slate-100 text-slate-800';
  };

  const getApprovalStage = (approval) => {
    if (approval.status === 'approved') return 'Fully Approved';
    if (approval.status === 'rejected') return 'Rejected';
    if (approval.consultant_approved) return 'Pending HOD Approval';
    return 'Pending Consultant Approval';
  };

  return (
    <div data-testid="medication-approvals-page">
      <header className="page-header glass-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Medication Approvals</h1>
            <p className="text-slate-500">Request and approve medications for indoor patients</p>
          </div>
          {canRequest && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-red-500 hover:bg-red-600 rounded-full" data-testid="request-medication-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Request Medication
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-[Manrope]">Request Medication Approval</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div>
                    <Label>Admitted Patient *</Label>
                    <Select value={formData.admission_id} onValueChange={handleAdmissionChange}>
                      <SelectTrigger data-testid="select-admission">
                        <SelectValue placeholder="Select patient" />
                      </SelectTrigger>
                      <SelectContent position="popper" sideOffset={5}>
                        {admissions.map(adm => (
                          <SelectItem key={adm.id} value={adm.id}>
                            {adm.patient?.full_name} - Room {adm.room_number}, Bed {adm.bed_number}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Medication Name *</Label>
                    <Input
                      value={formData.medication_name}
                      onChange={(e) => setFormData({...formData, medication_name: e.target.value})}
                      placeholder="e.g., Morphine 10mg"
                      required
                      data-testid="input-medication"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label>Dosage *</Label>
                      <Input
                        value={formData.dosage}
                        onChange={(e) => setFormData({...formData, dosage: e.target.value})}
                        placeholder="e.g., 10mg"
                        required
                        data-testid="input-dosage"
                      />
                    </div>
                    <div>
                      <Label>Frequency *</Label>
                      <Input
                        value={formData.frequency}
                        onChange={(e) => setFormData({...formData, frequency: e.target.value})}
                        placeholder="e.g., 6 hourly"
                        required
                        data-testid="input-frequency"
                      />
                    </div>
                    <div>
                      <Label>Duration *</Label>
                      <Input
                        value={formData.duration}
                        onChange={(e) => setFormData({...formData, duration: e.target.value})}
                        placeholder="e.g., 3 days"
                        required
                        data-testid="input-duration"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Urgency</Label>
                    <Select value={formData.urgency} onValueChange={(v) => setFormData({...formData, urgency: v})}>
                      <SelectTrigger data-testid="select-urgency">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent position="popper" sideOffset={5}>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                        <SelectItem value="critical">Critical</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Reason/Indication *</Label>
                    <Textarea
                      value={formData.reason}
                      onChange={(e) => setFormData({...formData, reason: e.target.value})}
                      placeholder="Clinical indication for this medication"
                      required
                      data-testid="input-reason"
                    />
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button type="submit" className="bg-red-500 hover:bg-red-600" data-testid="submit-request">Submit Request</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </header>

      <div className="page-content">
        {/* Info Banner */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <p className="font-medium text-blue-800">Approval Workflow</p>
            <p className="text-sm text-blue-600">
              Medications for indoor patients require two-stage approval: Consultant → HOD/Senior Doctor
            </p>
          </div>
        </div>

        <Card>
          <CardHeader className="border-b border-slate-100">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold font-[Manrope] flex items-center gap-2">
                <Pill className="w-5 h-5 text-red-500" />
                Medication Requests
              </CardTitle>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent position="popper" sideOffset={5}>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Loading...</div>
            ) : approvals.length === 0 ? (
              <div className="p-8 text-center text-slate-500">No medication approval requests found.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Patient</TableHead>
                    <TableHead>Medication</TableHead>
                    <TableHead>Dosage</TableHead>
                    <TableHead>Requested By</TableHead>
                    <TableHead>Stage</TableHead>
                    <TableHead>Status</TableHead>
                    {canApprove && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {approvals.map((approval) => (
                    <TableRow key={approval.id} data-testid={`approval-row-${approval.id}`}>
                      <TableCell className="font-mono text-sm">{approval.approval_id}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{approval.patient?.full_name}</p>
                          <p className="text-xs text-slate-500">{approval.patient?.patient_id}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{approval.medication_name}</p>
                          {approval.urgency !== 'normal' && (
                            <Badge className={approval.urgency === 'critical' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}>
                              {approval.urgency}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{approval.dosage} | {approval.frequency} | {approval.duration}</span>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm">{approval.requested_by_name}</p>
                          <p className="text-xs text-slate-500 capitalize">{approval.requested_by_role?.replace('_', ' ')}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3 text-slate-400" />
                          <span className="text-xs">{getApprovalStage(approval)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusBadge(approval.status)}>{approval.status}</Badge>
                      </TableCell>
                      {canApprove && (
                        <TableCell className="text-right">
                          {approval.status === 'pending' && (
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleApprove(approval.id)}
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                data-testid={`approve-${approval.id}`}
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openRejectDialog(approval)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`reject-${approval.id}`}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">Reject Medication Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Reason for Rejection</Label>
              <Textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter reason for rejection"
                data-testid="rejection-reason"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
              <Button className="bg-red-500 hover:bg-red-600" onClick={handleReject} data-testid="confirm-reject">Reject</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MedicationApprovals;
